import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, FileJson, Library, Loader2, Lock, Save, Trash2, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { saveBlob } from "@/lib/download";
import ConfirmationModal from "../common/ConfirmationModal";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import ConflictRulesRepository from "../../repositories/ConflictRulesRepository";
import { SavedConflictRule } from "../../models/ConflictRule";

/** The server's limit on a saved rule's description. */
const MAX_DESCRIPTION_LENGTH = 1000;

/** Above this an imported file cannot be a rule the server accepts, so it is not read at all. */
const MAX_IMPORT_BYTES = 64 * 1024;

/** A file name for the rule's JSON, from its name. */
const exportFileName = (name: string | null): string => {
  const slug = (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return `${slug || "conflict-rule"}.json`;
};

interface ConflictRuleLibraryPanelProps {
  /** The rule in the form, as the text a save stores and an export writes. */
  ruleText: string;
  ruleName: string | null;
  /**
   * Why a save would be refused now: the rule has errors, or has not been checked since it last changed. Only errors
   * are explained here; the page's action slot already says when the rule is being checked.
   */
  saveBlocked: "checking" | "errors" | null;
  /** The saved rule the form was loaded from, which Save updates. */
  loadedRule: SavedConflictRule | null;
  /** Puts a rule into the form: a preset, a saved rule (with the entry Save will update) or an imported file. */
  onUse: (ruleText: string, savedRule: SavedConflictRule | null) => void;
  /** A save stored the form's rule as `savedRule`. */
  onSaved: (savedRule: SavedConflictRule) => void;
  onDeleted: (id: string) => void;
  disabled?: boolean;
}

/**
 * The shared rule library, the same in every organization: the built-in rules and every saved rule. "Use" copies a rule into the form; Save, Save as and Delete change the library; Import and
 * Export move the form's rule in and out as a JSON file of the same shape.
 */
const ConflictRuleLibraryPanel: React.FC<ConflictRuleLibraryPanelProps> = ({
  ruleText,
  ruleName,
  saveBlocked,
  loadedRule,
  onUse,
  onSaved,
  onDeleted,
  disabled,
}) => {
  const repository = new ConflictRulesRepository();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saveMode, setSaveMode] = useState<"update" | "create" | null>(null);
  const [description, setDescription] = useState("");
  const [ruleToDelete, setRuleToDelete] = useState<SavedConflictRule | null>(null);

  const libraryQuery = useQuery({
    queryKey: [QUERY_KEYS.CONFLICT_RULE_LIBRARY],
    queryFn: () => repository.getLibrary(),
  });

  const refreshLibrary = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONFLICT_RULE_LIBRARY] });

  const saveMutation = useMutation({
    mutationFn: ({ mode, text }: { mode: "update" | "create"; text: string }) =>
      mode === "update" && loadedRule
        ? repository.updateRule(loadedRule.id, text, description.trim() || null, loadedRule.version)
        : repository.createRule(text, description.trim() || null),
    onSuccess: async (saved) => {
      setSaveMode(null);
      onSaved(saved);
      toast.success(`Saved "${saved.name}" to the library.`);
      await refreshLibrary();
    },
    // A refusal can mean someone saved the rule since it was loaded, so the listing is fetched again: Use then loads
    // their version, which the next Save is checked against.
    onError: async (error: Error) => {
      toast.error(error.message);
      await refreshLibrary();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (rule: SavedConflictRule) => repository.deleteRule(rule.id),
    onSuccess: async (_, rule) => {
      setRuleToDelete(null);
      onDeleted(rule.id);
      toast.success(`Deleted "${rule.name}" from the library.`);
      await refreshLibrary();
    },
    onError: (error: Error) => {
      setRuleToDelete(null);
      toast.error(error.message);
    },
  });

  const openSave = (mode: "update" | "create") => {
    setDescription(mode === "update" ? (loadedRule?.description ?? "") : "");
    setSaveMode(mode);
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMPORT_BYTES) {
      toast.error("The file is too large to be a rule.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onUse(reader.result, null);
    };
    reader.onerror = () => toast.error("The file could not be read.");
    reader.readAsText(file);
  };

  const hasName = (ruleName ?? "").trim().length > 0;
  const saveDisabled = disabled || !hasName || saveBlocked !== null;
  // Shown as text, not a title: a disabled button never shows its tooltip.
  const saveBlockedReason = disabled
    ? null
    : !hasName
      ? "Give the rule a name to save it."
      : saveBlocked === "errors"
        ? "Fix the rule's errors to save it."
        : null;
  const library = libraryQuery.data;

  return (
    <div className="flex flex-col gap-4 rounded-md border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Library className="size-4" />
          Rule library
        </h3>
        <div className="flex flex-wrap gap-2">
          <input type="file" accept=".json,application/json" className="hidden" ref={fileInputRef} onChange={handleImport} />
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
            <Upload className="size-4" />
            Import JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => saveBlob(new Blob([ruleText], { type: "application/json" }), exportFileName(ruleName))}
          >
            <Download className="size-4" />
            Export JSON
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {loadedRule && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => openSave("update")}
            disabled={saveDisabled}
            aria-describedby={saveBlockedReason ? "conflict-rule-save-blocked" : undefined}
          >
            <Save className="size-4" />
            Save
          </Button>
        )}
        <Button
          variant={loadedRule ? "outline" : "default"}
          size="sm"
          className="gap-1.5"
          onClick={() => openSave("create")}
          disabled={saveDisabled}
          aria-describedby={saveBlockedReason ? "conflict-rule-save-blocked" : undefined}
        >
          <Save className="size-4" />
          Save as new rule
        </Button>
        <span className="text-xs text-muted-foreground">
          {loadedRule ? (
            <>
              Editing <span className="font-medium text-foreground">{loadedRule.name}</span> (version{" "}
              {loadedRule.version})
            </>
          ) : (
            "Saved rules are shared by every organization."
          )}
        </span>
        {saveBlockedReason && (
          <p id="conflict-rule-save-blocked" className="basis-full text-xs text-muted-foreground">
            {saveBlockedReason}
          </p>
        )}
      </div>

      {libraryQuery.isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {libraryQuery.isError && (
        <p className="text-sm text-destructive">{(libraryQuery.error as Error).message}</p>
      )}

      {library && (
        <>
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Built-in rules
            </h4>
            {library.presets.map((preset) => (
              <div key={preset.key} className="flex items-center gap-2 rounded-md border px-3 py-2">
                <Lock className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 text-sm">{preset.name}</span>
                <Button variant="outline" size="sm" onClick={() => onUse(preset.definition, null)} disabled={disabled}>
                  Use
                </Button>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Saved rules</h4>
            {library.rules.length === 0 && (
              <p className="text-sm text-muted-foreground">No saved rules yet.</p>
            )}
            {library.rules.map((saved) => (
              <div
                key={saved.id}
                className={cn(
                  "flex items-start gap-2 rounded-md border px-3 py-2",
                  loadedRule?.id === saved.id && "border-primary/60 bg-primary/5"
                )}
              >
                <FileJson className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="break-words">{saved.name}</span>
                    {loadedRule?.id === saved.id && <Badge variant="outline-primary">In the form</Badge>}
                  </p>
                  {saved.description && (
                    <p className="line-clamp-2 text-xs text-muted-foreground">{saved.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Version {saved.version} · {saved.modifiedBy ?? saved.createdBy},{" "}
                    {convertUtcToLocal(saved.modifiedOn ?? saved.createdOn)}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => onUse(saved.definition, saved)} disabled={disabled}>
                  Use
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setRuleToDelete(saved)}
                      disabled={disabled}
                      aria-label={`Delete ${saved.name}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete from the library</TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={saveMode !== null} onOpenChange={(open) => !open && !saveMutation.isPending && setSaveMode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{saveMode === "update" ? "Save changes" : "Save as a new rule"}</DialogTitle>
            <DialogDescription>
              {saveMode === "update"
                ? `Saves the form as version ${(loadedRule?.version ?? 0) + 1} of "${loadedRule?.name}". The rule's name in the form names it.`
                : `Saves the form to the library as "${ruleName ?? ""}". Every organization sees it.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="conflict-rule-description">Description (optional)</Label>
            <Textarea
              id="conflict-rule-description"
              rows={4}
              value={description}
              maxLength={MAX_DESCRIPTION_LENGTH}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="When to use the rule, and what it expects of the groups"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveMode(null)} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMode && saveMutation.mutate({ mode: saveMode, text: ruleText })}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ruleToDelete && (
        <ConfirmationModal
          open
          onClose={() => !deleteMutation.isPending && setRuleToDelete(null)}
          onConfirm={() => deleteMutation.mutate(ruleToDelete)}
          message={`Delete "${ruleToDelete.name}" from the library for every organization? Runs made with it keep their own copy of the rule.`}
          statusColor="danger"
          buttonText="Delete"
          confirmDisabled={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default ConflictRuleLibraryPanel;
